import { CheckCircleIcon, ArrowPathIcon } from "@heroicons/react/24/outline";
import { ChangeEvent, useCallback, useEffect, useState, useRef } from "react";

import Input, { InputRef } from "antd/lib/input";
import { SizeType } from "antd/lib/config-provider/SizeContext";
import { useNotification } from "hooks/useNotification";

interface Props {
    defaultValue?: string;
    placeholder?: string;
    onSave: (userInput: string, id?: string) => Promise<void>;
    className?: string;
    size?: SizeType;
    isLoading?: boolean;
    resetOnSave?: boolean;
    maxLength?: number;
}
const NameEdit = ({
    defaultValue,
    placeholder,
    onSave,
    className,
    size = "middle",
    isLoading,
    resetOnSave,
    maxLength,
}: Props) => {
    const notify = useNotification();

    const inputRef = useRef<InputRef>(null);

    const [isEditing, setIsEditing] = useState(false);

    const [userInput, setUserInput] = useState(defaultValue ?? "");

    const [isSaveable, setIsSaveable] = useState(false);

    const handleInputChange = useCallback(
        (event: ChangeEvent<HTMLInputElement>) => {
            const input = event.currentTarget.value;
            setUserInput(input);
        },
        [],
    );

    const handleSave = useCallback(async () => {
        if (!isSaveable) {
            console.log("not saveable");

            return;
        }
        setIsSaveable(false);
        setIsEditing(false);
        await onSave(userInput);
        if (resetOnSave) {
            setUserInput("");
        }
        inputRef.current?.blur();
    }, [isSaveable, onSave, userInput, resetOnSave, inputRef]);

    useEffect(() => {
        if (defaultValue) {
            setUserInput(defaultValue);
        }
    }, [defaultValue]);

    useEffect(() => {
        setIsSaveable(userInput !== defaultValue && !!userInput);
    }, [defaultValue, userInput]);

    return (
        // eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions
        <form
            className={`inline-flex cursor-text gap-1 ${className}`}
            onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
            }}
            onSubmit={(e) => {
                e.preventDefault();
                e.stopPropagation();
                if ((isEditing && isSaveable) || userInput.length === 0) {
                    setIsSaveable(false);
                    handleSave();
                }
                if (!isSaveable) {
                    setUserInput(defaultValue ?? "");
                }
                if (userInput.length === 0) {
                    notify.error("Name cannot be empty");
                }
            }}
        >
            <Input
                size={size}
                id="namedit"
                placeholder={placeholder}
                value={userInput}
                variant={isEditing ? "outlined" : "borderless"}
                ref={inputRef}
                className={userInput.length === 0 ? "w-full" : "w-max"}
                htmlSize={
                    userInput.length > 0
                        ? userInput.length + 1
                        : (placeholder?.length ?? undefined)
                }
                maxLength={maxLength}
                suffix={
                    <button
                        disabled={isLoading || isEditing}
                        onClick={(e) => {
                            if (isLoading || (isSaveable && !isEditing)) {
                                e.preventDefault();
                                e.stopPropagation();
                            }
                        }}
                        type="button"
                        title={
                            isEditing
                                ? isSaveable
                                    ? "Save"
                                    : undefined
                                : "Edit name"
                        }
                    >
                        {isLoading ? (
                            <ArrowPathIcon className="size-4 animate-spin-faster text-secondary-black" />
                        ) : isEditing ? (
                            isSaveable ? (
                                <CheckCircleIcon className="size-5 text-primary-green" />
                            ) : (
                                <CheckCircleIcon className="size-5 text-secondary-black" />
                            )
                        ) : null}
                    </button>
                }
                onClick={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                }}
                onChange={handleInputChange}
                onBlur={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    if (isEditing && isSaveable && !isLoading) {
                        handleSave();
                    } else {
                        setIsEditing(false);
                    }
                }}
                onFocus={() => {
                    setIsEditing(true);
                }}
            />
        </form>
    );
};

export default NameEdit;
